import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Money, Clock, Users, FileText, ArrowRight, Plus, UploadSimple,
  DownloadSimple, Trash, ArrowsClockwise, IdentificationCard, EnvelopeSimple,
} from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { RM } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { EmployeeDialog } from "@/components/EmployeeDialog";
import { EmployeeImportDialog } from "@/components/EmployeeImportDialog";
import { Button } from "@/components/ui/button";

const QUICK = [
  { to: "/payroll", label: "Payroll Process", desc: "Create runs & compute statutory pay", icon: Money, tone: "text-red-500", ring: "hover:border-red-500/40" },
  { to: "/attendance", label: "Attendance", desc: "Overtime & unpaid leave per run", icon: Clock, tone: "text-blue-400", ring: "hover:border-blue-500/40" },
  { to: "/employees", label: "Employees Data", desc: "Manage employee records", icon: Users, tone: "text-emerald-400", ring: "hover:border-emerald-500/40" },
  { to: "/payslips", label: "Payslips & EA Form", desc: "Generate & download documents", icon: FileText, tone: "text-violet-400", ring: "hover:border-violet-500/40" },
];

export default function AdminHub() {
  const navigate = useNavigate();
  const [count, setCount] = useState(null);
  const [runCount, setRunCount] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => {
    api.get("/employees").then(({ data }) => setCount(data.length)).catch(() => {});
    api.get("/payroll/runs").then(({ data }) => setRunCount(data.length)).catch(() => {});
  };
  useEffect(load, []);

  const download = (path, filename) => {
    const token = localStorage.getItem("pf_token");
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <Layout title="Administrator Hub" subtitle="Everything you need to run payroll, in one place">
      {/* Quick access */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {QUICK.map((q, i) => (
          <button
            key={q.to}
            data-testid={`hub-${q.to.slice(1)}`}
            onClick={() => navigate(q.to)}
            style={{ animationDelay: `${i * 60}ms` }}
            className={`group animate-fade-up flex flex-col items-start border border-[#E2E5EA] bg-[#FFFFFF] p-5 text-left transition-colors duration-200 ${q.ring}`}
          >
            <div className={`flex h-11 w-11 items-center justify-center rounded-md bg-[#EEF1F5] ${q.tone}`}>
              <q.icon size={22} weight="bold" />
            </div>
            <p className="mt-4 font-heading text-base font-semibold text-[#0F172A]">{q.label}</p>
            <p className="mt-1 text-xs text-[#9CA3AF]">{q.desc}</p>
            <span className={`mt-3 flex items-center text-sm ${q.tone} opacity-0 transition-opacity duration-200 group-hover:opacity-100`}>
              Open <ArrowRight size={14} className="ml-1" />
            </span>
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Employee data management */}
        <div className="border border-[#E2E5EA] bg-[#FFFFFF] p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#0F172A]">Employee Data</h3>
              <p className="mt-0.5 text-sm text-[#9CA3AF]">
                {count === null ? "…" : `${count} employees on record`} · {runCount} payroll run{runCount === 1 ? "" : "s"}
              </p>
            </div>
            <IdentificationCard size={26} className="text-[#C7CDD6]" />
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Button data-testid="hub-add-employee" onClick={() => setAddOpen(true)} className="justify-start bg-red-500 font-semibold text-white hover:bg-red-600">
              <Plus size={16} weight="bold" className="mr-2" /> Add Employee
            </Button>
            <Button data-testid="hub-import" onClick={() => setImportOpen(true)} variant="outline" className="justify-start border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-red-500">
              <UploadSimple size={16} className="mr-2" /> Migrate / Import (CSV · Excel)
            </Button>
            <Button data-testid="hub-export" onClick={() => download("/employees/export", "employees.xlsx")} variant="outline" className="justify-start border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-emerald-400">
              <DownloadSimple size={16} className="mr-2" /> Download Employee Excel
            </Button>
            <Button data-testid="hub-export-csv" onClick={() => download("/employees/export/csv", "employees.csv")} variant="outline" className="justify-start border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-red-500">
              <DownloadSimple size={16} className="mr-2" /> Download Employee CSV
            </Button>
            <Button data-testid="hub-remove" onClick={() => navigate("/employees")} variant="outline" className="justify-start border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-red-400">
              <Trash size={16} className="mr-2" /> Add / Remove Employees
            </Button>
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-md border border-[#E2E5EA] bg-[#F4F5F7] p-3 text-xs text-[#9CA3AF]">
            <ArrowsClockwise size={15} className="mt-0.5 shrink-0 text-red-500" />
            <span>
              Download the Excel to migrate or edit records in bulk, then re-upload it — matched employees update
              automatically and the refreshed data feeds straight into your next payroll run.
            </span>
          </div>
        </div>

        {/* Process shortcut */}
        <div className="flex flex-col border border-[#E2E5EA] bg-[#FFFFFF] p-6">
          <h3 className="font-heading text-lg font-semibold text-[#0F172A]">Run Payroll</h3>
          <p className="mt-1 flex-1 text-sm text-[#9CA3AF]">
            Once employee data is in place, start a monthly run. Attendance, overtime and statutory
            contributions all flow into the calculation.
          </p>
          <Button data-testid="hub-start-run" onClick={() => navigate("/payroll")} className="mt-4 bg-red-500 font-semibold text-white hover:bg-red-600">
            <Money size={16} className="mr-1.5" /> Go to Payroll Process
          </Button>
          <Button data-testid="hub-payslips" onClick={() => navigate("/payslips")} variant="outline" className="mt-2 border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-violet-400">
            <EnvelopeSimple size={16} className="mr-1.5" /> Payslips & EA Forms
          </Button>
        </div>
      </div>

      <EmployeeDialog open={addOpen} onOpenChange={setAddOpen} employee={null} onSaved={load} />
      <EmployeeImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
    </Layout>
  );
}
